import React, { useRef, useState } from "react";
import {
  CaptchaFrameProps,
  ChallengeSelectorConfig,
  ContestId,
  RunningChallengeType,
  SelectableChallengeType,
} from "../models/Types";
import Challenge1DDemo from "./help/Challenge1DDemo";
import Challenge2DDemo from "./help/Challenge2DDemo";
import ProtonCaptcha from "./ProtonCaptcha";
import { useTranslation } from "react-i18next";
import ExplosionAnimation from "./ExplosionAnimation";
import { utils } from "pixi.js-legacy";
import API from "../api/captcha";
import { getPurpose, isDarkModeEnabled } from "./Util";

function ChallengeSelector(props: ChallengeSelectorConfig) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-column">
      <p className={"no-top-margin"}>{t("Select.text")}</p>
      <div className="columns equal-columns">
        <button
          type="button"
          aria-pressed={props.selectedChallengeType == "1D"}
          className={`col captcha-option flex flex-column text-center ${props.selectedChallengeType == "1D" ? "selected-captcha" : ""}`}
          onClick={props.onClick1D}
        >
          <Challenge1DDemo width={140} height={150} />
          <div className="mtauto selected-container">
            {props.selectedChallengeType == "1D" && (
              <div className={"selected-indicator"}>{t("Selected.text")}</div>
            )}
          </div>
        </button>

        <button
          type="button"
          aria-pressed={props.selectedChallengeType == "2D"}
          className={`col captcha-option flex flex-column text-center ${props.selectedChallengeType == "2D" ? "selected-captcha" : ""}`}
          onClick={props.onClick2D}
        >
          <Challenge2DDemo width={140} height={150} />
          <div className="mtauto selected-container">
            {props.selectedChallengeType == "2D" && (
              <div className={"selected-indicator"}>{t("Selected.text")}</div>
            )}
          </div>
        </button>
      </div>
      <br />
      <div>
        <div className="text-center">
          <button
            type="button"
            className={
              "btn rounded-full btn-small btn-solid-purple btn-full-width"
            }
            onClick={props.onClickNext}
            disabled={props.disabled}
          >
            {t("Next.text")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CaptchaFrame(props: CaptchaFrameProps) {
  const [runningChallengeType, setRunningChallengeType] =
    useState<RunningChallengeType>("2D");
  const [selectedChallengeType, setSelectedChallengeType] =
    useState<SelectableChallengeType>("2D");
  const [verified, setVerified] = useState(false);
  const [viChallengeInProgress, setViChallengeInProgress] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");

  const contestId = useRef<ContestId>("");

  utils.skipHello();

  const { t } = useTranslation();
  const displayedLanguage = t("LangCode");
  const isRtl = displayedLanguage == "fa";

  function showCompletedAnimation(captchaToken: string) {
    setCaptchaToken(captchaToken);
    setVerified(true);
  }

  function terminateCaptchaApp() {
    API.finalize(props.apiURL, contestId.current, getPurpose());

    const message = {
      type: "proton_captcha",
      token: captchaToken,
      protoncaptcha: true, // this is just a marker to differentiate this message from other 'pm_captcha' ones
    };
    window.parent.postMessage(message, "*");
  }

  function displayChallengeSelector() {
    setViChallengeInProgress(false);
    setVerified(false);
    setRunningChallengeType("selector");
  }

  return (
    <div
      className={`protonCaptchaContainer ${isDarkModeEnabled() ? "dark" : ""}`}
      dir={`${isRtl ? "rtl" : "ltr"}`}
    >
      {!verified &&
        (runningChallengeType === "selector" ||
          runningChallengeType === "VI") && (
          <ChallengeSelector
            selectedChallengeType={selectedChallengeType}
            onClick1D={() => setSelectedChallengeType("1D")}
            onClick2D={() => setSelectedChallengeType("2D")}
            onClickNext={() => setRunningChallengeType(selectedChallengeType)}
            disabled={viChallengeInProgress}
            verified={verified}
            width={props.width}
            height={props.height}
            apiBaseURL={props.apiURL}
            successCallback={showCompletedAnimation}
          />
        )}

      {!verified && runningChallengeType === "1D" && (
        <ProtonCaptcha
          titleText={"Beam Challenge"}
          promptText={t("Challenge1DPrompt.text")}
          apiBaseURL={props.apiURL}
          challengeType={"1D"}
          contestId={contestId}
          width={props.width}
          height={props.height}
          tryAnotherChallengeCallback={displayChallengeSelector}
          setViChallengeInProgressCallback={setViChallengeInProgress}
          successCallback={showCompletedAnimation}
        ></ProtonCaptcha>
      )}

      {!verified && runningChallengeType === "2D" && (
        <ProtonCaptcha
          titleText={"Puzzle Challenge"}
          promptText={t("Challenge2DPrompt.text")}
          apiBaseURL={props.apiURL}
          challengeType={"2D"}
          contestId={contestId}
          width={props.width}
          height={props.height}
          tryAnotherChallengeCallback={displayChallengeSelector}
          setViChallengeInProgressCallback={setViChallengeInProgress}
          successCallback={showCompletedAnimation}
        ></ProtonCaptcha>
      )}

      {!verified && runningChallengeType == "selector" && (
        <ProtonCaptcha
          titleText={""}
          promptText={""}
          apiBaseURL={props.apiURL}
          challengeType={"VI"}
          contestId={contestId}
          width={props.width}
          height={props.height}
          tryAnotherChallengeCallback={displayChallengeSelector}
          setViChallengeInProgressCallback={setViChallengeInProgress}
          successCallback={showCompletedAnimation}
        ></ProtonCaptcha>
      )}

      {verified && (
        <div className={"successful-info"}>
          <ExplosionAnimation
            width={props.width}
            height={props.height}
            callback={terminateCaptchaApp}
          />
        </div>
      )}
    </div>
  );
}
